/**
 * AAMVA Barcode Parser
 * Parses PDF417 barcode data from US driver's licenses according to AAMVA standard
 * Based on AAMVA DL/ID Card Design Standard (version 2020)
 */

export const parseAAMVA = (barcodeData) => {
  if (!barcodeData || typeof barcodeData !== 'string') {
    return null;
  }

  // AAMVA barcode format: @\nANSI...\n...data...
  // Data is separated by \n and fields are in format: DAA (field code) = (value)
  // Some formats use position-based encoding or different delimiters
  
  const result = {
    firstName: '',
    lastName: '',
    middleName: '',
    address: '',
    city: '',
    state: '',
    zipcode: '',
    driversLicense: '',
    dateOfBirth: '',
    expirationDate: '',
    gender: '',
  };

  try {
    // Clean the input - remove control characters but keep newlines
    let cleanedData = barcodeData.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F]/g, '');
    
    // FIRST: Search for DLDAQ pattern anywhere in the data (it might be in the ANSI header line)
    // Pattern: DLDAQ followed by the license number (alphanumeric, 6-20 chars)
    const dlnaqMatch = cleanedData.match(/DLDAQ([A-Z0-9]{6,20})/);
    if (dlnaqMatch) {
      const licenseNumber = dlnaqMatch[1];
      console.log(`✅ Found DLN in data (DLDAQ pattern): "${licenseNumber}"`);
      result.driversLicense = licenseNumber;
    }
    
    // Also search for DAQ field in the data (without DL prefix)
    // Pattern: DAQ followed by license number (can be at start of line or after space)
    const daqMatch = cleanedData.match(/(?:^|\s)DAQ([A-Z0-9]{4,20})/);
    if (daqMatch && !result.driversLicense) {
      const licenseNumber = daqMatch[1];
      console.log(`✅ Found DLN in data (DAQ pattern): "${licenseNumber}"`);
      result.driversLicense = licenseNumber;
    }
    
    // Split by newlines and process each line
    const lines = cleanedData.split(/[\n\r]+/).filter(line => line.trim().length > 0);
    
    console.log('=== PARSING AAMVA BARCODE ===');
    console.log('Total lines:', lines.length);
    console.log('First few lines:', lines.slice(0, 10));
    
    // First pass: Look for standard AAMVA format with = separator
    for (let line of lines) {
      const trimmedLine = line.trim();
      
      // Check ANSI header line for DLDAQ pattern before skipping
      if (trimmedLine.startsWith('@') || trimmedLine.startsWith('ANSI')) {
        // Search for DLDAQ in ANSI header line
        const ansiDlnaqMatch = trimmedLine.match(/DLDAQ([A-Z0-9]{6,20})/);
        if (ansiDlnaqMatch && !result.driversLicense) {
          const licenseNumber = ansiDlnaqMatch[1];
          console.log(`✅ Found DLN in ANSI header: "${licenseNumber}"`);
          result.driversLicense = licenseNumber;
        }
        continue;
      }

      // Handle DL prefix format: DLDAQD03442650 means DAQ field with value D03442650
      // Format: DL + 3-char field code + value (no separator)
      // THIS MUST BE CHECKED FIRST before other formats
      // Also check if DL pattern exists anywhere in the line (for ANSI header)
      const dlPattern = trimmedLine.match(/DLDAQ([A-Z0-9]{6,20})/);
      if (dlPattern) {
        const licenseNumber = dlPattern[1];
        console.log(`✅ Found DLDAQ in line: "${licenseNumber}"`);
        result.driversLicense = licenseNumber; // Always set this, it's the most reliable
      }
      
      if (trimmedLine.startsWith('DL') && trimmedLine.length > 5) {
        const fieldCode = trimmedLine.substring(2, 5); // Extract DAQ from DLDAQ
        const value = trimmedLine.substring(5).trim(); // Extract D03442650
        console.log(`🔍 Found DL-prefixed field: ${fieldCode} = "${value}"`);
        
        // Process DL-prefixed fields
        switch (fieldCode) {
          case 'DAQ': // Driver License Number - THIS IS THE PRIMARY DLN FIELD
            console.log(`✅ Setting DLN from DLDAQ: "${value}"`);
            result.driversLicense = value; // Always set this, it's the most reliable
            break;
          case 'DCS': // Last Name (in some formats)
            if (!result.lastName) {
              result.lastName = value;
            }
            break;
          case 'DAC': // First Name
            if (!result.firstName) {
              result.firstName = value;
            }
            break;
          case 'DAD': // Middle Name
            result.middleName = value;
            break;
        }
        continue; // Skip to next line - don't process this line further
      }

      // AAMVA format: DAA = value or DAAvalue (without space)
      // Also handle cases where field code might be at the start of line without =
      if (trimmedLine.includes('=')) {
        const line = trimmedLine; // Use trimmed line
        const [key, ...valueParts] = trimmedLine.split('=');
        const value = valueParts.join('=').trim();
        const fieldCode = key.trim();
        
        // Log all field codes for debugging
        if (fieldCode.startsWith('DA') || fieldCode.startsWith('DC') || fieldCode.startsWith('DD')) {
          console.log(`Found AAMVA field: ${fieldCode} = ${value.substring(0, 50)}`);
        }

        // Parse common AAMVA field codes (AAMVA DL/ID Card Design Standard)
        // IMPORTANT: Order matters - check more specific codes first
        switch (fieldCode) {
          // Driver License Number - check FIRST before other D codes
          // IMPORTANT: DAQ is the PRIMARY field for DLN, always use it if found
          case 'DAQ': // Driver License Number (most common) - PRIMARY FIELD
            console.log(`✅ Setting DLN from DAQ: ${value}`);
            result.driversLicense = value; // Always set, this is the most reliable
            break;
          case 'DCQ': // Driver License Number (alternate)
            if (!result.driversLicense) {
              console.log(`Setting DLN from DCQ: ${value}`);
              result.driversLicense = value;
            }
            break;
          case 'DCF': // Document Discriminator - DO NOT USE FOR LICENSE NUMBER
            // DCF is a document discriminator (like 123200ED7B033005), NOT the license number
            // NEVER use DCF for license number - it's metadata
            console.log(`⚠️ Ignoring DCF (document discriminator, not DLN): ${value}`);
            break;
          
          // DCS is Last Name in some formats, not license number
          case 'DCS':
            if (!result.lastName) {
              result.lastName = value;
            }
            break;
          
          case 'DAC': // First Name
          case 'DDF': // First Name (version 2016+)
            if (!result.firstName) {
              result.firstName = value;
            }
            break;
          
          // DCT can be First Name OR License Number depending on state - check value pattern
          case 'DCT': 
            // If it looks like a license number (alphanumeric, no spaces, 6+ chars), it's a license
            if (/^[A-Z0-9]{6,}$/.test(value) && !result.driversLicense) {
              result.driversLicense = value;
            } else if (!result.firstName && !/^[A-Z]{2,}$/.test(value)) {
              // If it's all caps and short, might be a name - but not if it looks like LASTNAME
              // Otherwise it's a first name
              result.firstName = value;
            }
            break;
          
          case 'DAD': // Last Name
          case 'DCU': // Last Name (alternate)
          case 'DDG': // Last Name (version 2016+)
            if (!result.lastName) {
              result.lastName = value;
            }
            break;
          
          case 'DAB': // Middle Name
          case 'DCV': // Middle Name (alternate)
          case 'DDH': // Middle Name (version 2016+)
            result.middleName = value;
            break;
          
          case 'DAG': // Street Address
          case 'DCG': // Street Address (alternate)
          case 'DDI': // Street Address (version 2016+)
            if (!result.address) {
              result.address = value;
            }
            break;
          
          case 'DAI': // City
          case 'DCI': // City (alternate)
          case 'DDJ': // City (version 2016+)
            if (!result.city) {
              result.city = value;
            }
            break;
          
          case 'DAJ': // State (2-letter code)
          case 'DCJ': // State (alternate)
          case 'DDK': // State (version 2016+)
            if (!result.state) {
              result.state = value;
            }
            break;
          
          case 'DAK': // ZIP Code - PRIMARY ZIP CODE FIELD
            // ZIP codes are 5 digits, sometimes with 4-digit extension
            // Clean and format the zip code - take only first 5 digits
            const zipOnly = value.replace(/\D/g, ''); // Remove non-digits
            if (zipOnly.length >= 5) {
              result.zipcode = zipOnly.substring(0, 5); // Take ONLY first 5 digits
            } else if (zipOnly.length > 0) {
              result.zipcode = zipOnly; // Use what we have if less than 5 digits
            } else {
              result.zipcode = value.trim(); // Fallback to original if no digits
            }
            console.log(`✅ Setting ZIP from DAK (PRIMARY): "${result.zipcode}" (original: "${value}")`);
            break;
          case 'DCK': // ZIP Code (alternate) - Only use if DAK not already set
            // DCK might be document control number in some formats, not always ZIP
            // Only use if we don't have a ZIP from DAK yet
            if (!result.zipcode) {
              const zipOnlyDCK = value.replace(/\D/g, ''); // Remove non-digits
              if (zipOnlyDCK.length >= 5) {
                result.zipcode = zipOnlyDCK.substring(0, 5); // Take ONLY first 5 digits
              } else if (zipOnlyDCK.length > 0) {
                result.zipcode = zipOnlyDCK;
              } else {
                result.zipcode = value.trim();
              }
              console.log(`Setting ZIP from DCK (fallback): "${result.zipcode}" (original: "${value}")`);
            } else {
              console.log(`Skipping DCK - ZIP already set from DAK: "${result.zipcode}"`);
            }
            break;
          case 'DDL': // ZIP Code (version 2016+)
            // Only use if DAK not already set
            if (!result.zipcode) {
              const zipOnlyDDL = value.replace(/\D/g, ''); // Remove non-digits
              if (zipOnlyDDL.length >= 5) {
                result.zipcode = zipOnlyDDL.substring(0, 5); // Take ONLY first 5 digits
              } else if (zipOnlyDDL.length > 0) {
                result.zipcode = zipOnlyDDL;
              } else {
                result.zipcode = value.trim();
              }
              console.log(`Setting ZIP from DDL: "${result.zipcode}" (original: "${value}")`);
            }
            break;
          
          case 'DAQ': // Driver License Number (most common)
          case 'DCQ': // Driver License Number (alternate)
          case 'DCS': // Driver License Number (some states)
          case 'DCF': // Driver License Number (some states)
            if (!result.driversLicense) {
              result.driversLicense = value;
            }
            break;
          
          case 'DBB': // Date of Birth (MMDDYYYY)
          case 'DCB': // Date of Birth (alternate)
            result.dateOfBirth = value;
            break;
          
          case 'DBA': // Expiration Date (MMDDYYYY)
          case 'DCA': // Expiration Date (alternate)
            result.expirationDate = value;
            break;
          
          case 'DBC': // Gender (1=M, 2=F)
          case 'DCC': // Gender (alternate)
            result.gender = value === '1' ? 'M' : value === '2' ? 'F' : value;
            break;
        }
      } else if (trimmedLine.length > 3 && /^D[A-Z]{2}/.test(trimmedLine) && !trimmedLine.startsWith('DL')) {
        // Handle format without = separator (some states use this)
        // Format: DCSLASTNAME, DACFIRSTNAME, etc.
        // Skip if it starts with DL (already handled above)
        const fieldCode = trimmedLine.substring(0, 3);
        const value = trimmedLine.substring(3).trim();
        
        console.log(`🔍 Found field (no separator): ${fieldCode} = "${value.substring(0, 30)}"`);
        
        switch (fieldCode) {
          case 'DAC':
          case 'DCT':
          case 'DDF':
            if (!result.firstName) result.firstName = value;
            break;
          case 'DAD':
          case 'DCU':
          case 'DDG':
            if (!result.lastName) result.lastName = value;
            break;
          case 'DAG':
          case 'DCG':
          case 'DDI':
            if (!result.address) result.address = value;
            break;
          case 'DAI':
          case 'DCI':
          case 'DDJ':
            if (!result.city) result.city = value;
            break;
          case 'DAJ':
          case 'DCJ':
          case 'DDK':
            if (!result.state) result.state = value;
            break;
          case 'DAK': // ZIP Code - PRIMARY ZIP CODE FIELD
            // ZIP codes are 5 digits, sometimes with extension
            // Take ONLY first 5 digits
            const zipOnlyNoSep = value.replace(/\D/g, ''); // Remove non-digits
            if (zipOnlyNoSep.length >= 5) {
              result.zipcode = zipOnlyNoSep.substring(0, 5); // Take ONLY first 5 digits
            } else if (zipOnlyNoSep.length > 0) {
              result.zipcode = zipOnlyNoSep; // Use what we have if less than 5 digits
            } else {
              result.zipcode = value.trim(); // Fallback
            }
            console.log(`✅ Setting ZIP from DAK (no separator, PRIMARY): "${result.zipcode}" (original: "${value}")`);
            break;
          case 'DCK': // ZIP Code (alternate) - Only use if DAK not already set
            // DCK might be document control number in some formats, not always ZIP
            if (!result.zipcode) {
              const zipOnlyDCKNoSep = value.replace(/\D/g, ''); // Remove non-digits
              if (zipOnlyDCKNoSep.length >= 5) {
                result.zipcode = zipOnlyDCKNoSep.substring(0, 5); // Take ONLY first 5 digits
              } else if (zipOnlyDCKNoSep.length > 0) {
                result.zipcode = zipOnlyDCKNoSep;
              } else {
                result.zipcode = value.trim();
              }
              console.log(`Setting ZIP from DCK (no separator, fallback): "${result.zipcode}" (original: "${value}")`);
            } else {
              console.log(`Skipping DCK (no separator) - ZIP already set from DAK: "${result.zipcode}"`);
            }
            break;
          case 'DDL': // ZIP Code (version 2016+)
            // Only use if DAK not already set
            if (!result.zipcode) {
              const zipOnlyDDLNoSep = value.replace(/\D/g, ''); // Remove non-digits
              if (zipOnlyDDLNoSep.length >= 5) {
                result.zipcode = zipOnlyDDLNoSep.substring(0, 5); // Take ONLY first 5 digits
              } else if (zipOnlyDDLNoSep.length > 0) {
                result.zipcode = zipOnlyDDLNoSep;
              } else {
                result.zipcode = value.trim();
              }
              console.log(`Setting ZIP from DDL (no separator): "${result.zipcode}" (original: "${value}")`);
            }
            break;
          case 'DAQ': // PRIMARY DLN field
            console.log(`✅ Setting DLN from DAQ (no separator): "${value}"`);
            result.driversLicense = value; // Always set
            break;
          case 'DCQ':
            if (!result.driversLicense) {
              console.log(`Setting DLN from DCQ (no separator): ${value}`);
              result.driversLicense = value;
            }
            break;
          case 'DCS':
            // DCS is last name in this format, not license number
            if (!result.lastName) result.lastName = value;
            break;
          // DO NOT use DD fields for DLN - they are document discriminators/metadata
          case 'DDA':
          case 'DDB':
          case 'DDC':
          case 'DDD':
          case 'DDE':
          case 'DDF':
          case 'DDG':
          case 'DDH':
          case 'DDI':
          case 'DDJ':
          case 'DDK':
          case 'DDL':
            // These are document discriminator or other metadata fields, NOT license number
            console.log(`Skipping DD field ${fieldCode} (not DLN): ${value.substring(0, 20)}`);
            break;
        }
      }
    }

    // Alternative parsing for formats that use position-based encoding
    // Some states use a different format where data is in fixed positions
    // This is a fallback if standard parsing didn't work
    if (!result.firstName && !result.lastName && !result.address) {
      // Try to find patterns in the raw data
      const fullText = cleanedData.replace(/\n/g, ' ');
      
      // Try to extract name patterns (Last, First Middle or First Last)
      const namePatterns = [
        /([A-Z][A-Z\s,]+),\s*([A-Z][A-Z\s]+)(?:\s+([A-Z][A-Z\s]+))?/, // Last, First Middle
        /([A-Z][A-Z\s]+)\s+([A-Z][A-Z\s]+)/, // First Last
      ];
      
      for (let pattern of namePatterns) {
        const nameMatch = fullText.match(pattern);
        if (nameMatch && nameMatch[1] && nameMatch[2]) {
          // Check if it's "Last, First" format
          if (nameMatch[1].includes(',')) {
            result.lastName = nameMatch[1].replace(',', '').trim();
            result.firstName = nameMatch[2].trim();
            if (nameMatch[3]) {
              result.middleName = nameMatch[3].trim();
            }
          } else {
            result.firstName = nameMatch[1].trim();
            result.lastName = nameMatch[2].trim();
            if (nameMatch[3]) {
              result.middleName = nameMatch[3].trim();
            }
          }
          break;
        }
      }
      
      // Try to extract address patterns
      const addressPattern = /(\d+\s+[A-Z0-9\s#\-\.]+(?:ST|STREET|AVE|AVENUE|RD|ROAD|BLVD|BOULEVARD|LN|LANE|DR|DRIVE|CT|COURT|PL|PLACE|WAY|CIR|CIRCLE))/i;
      const addressMatch = fullText.match(addressPattern);
      if (addressMatch && !result.address) {
        result.address = addressMatch[1].trim();
      }
      
      // Try to extract city, state, zip
      const cityStateZipPattern = /([A-Z][A-Z\s]+),\s*([A-Z]{2})\s+(\d{5}(?:-\d{4})?)/;
      const cityStateZipMatch = fullText.match(cityStateZipPattern);
      if (cityStateZipMatch && !result.city) {
        result.city = cityStateZipMatch[1].trim();
        result.state = cityStateZipMatch[2];
        result.zipcode = cityStateZipMatch[3];
      }
    }

    // Clean up values
    Object.keys(result).forEach(key => {
      if (typeof result[key] === 'string') {
        result[key] = result[key].trim();
      }
    });

    // Debug logging
    const hasAnyData = Object.values(result).some(val => val && val.length > 0);
    console.log('AAMVA Parser: Extracted data:', result);
    console.log('Driver License Number found:', result.driversLicense || 'NOT FOUND');
    
    if (!hasAnyData) {
      console.log('AAMVA Parser: No data extracted from barcode');
      console.log('Sample of input data:', cleanedData.substring(0, 500));
      console.log('Number of lines processed:', lines.length);
      console.log('All lines:', lines);
    }

    return result;
  } catch (error) {
    console.error('Error parsing AAMVA barcode:', error);
    return null;
  }
};
